import type {
    IModuleHotspotFields,
    IPageCampaigns,
    IPageExplore,
    IPageHomepage,
    IPageProductCategoryPage,
    IPageStories,
    IPdpConfiguration,
    IPdpConfigurationFields,
} from "@contentful-api/types/contentful";
import { getAlgoliaProduct } from "@lib/algolia/getAlgoliaProduct";
import { PageTypes } from "@lib/enums/PageTypes";
import structuredClone from "@ungap/structured-clone";
import type { Entry } from "contentful";

const processSingleHotspot = async (
    hotspot: IModuleHotspotFields["hotspotsDesktop"] | IModuleHotspotFields["hotspotMobile"],
    locale: string,
    pageType: PageTypes
) => {
    const product = hotspot?.hotspot?.fields?.product;
    const productKey = (product?.[locale] || product?.en) as string;

    if (productKey) {
        const attributesToRetrieve = [
            "name",
            "articleNumber",
            "colorCode",
            "descriptiveHeading",
            "image",
            "priceRaw",
            "listPriceRaw",
            "discountPercentage",
        ];
        const algoliaProduct = await getAlgoliaProduct(
            productKey,
            locale,
            pageType,
            attributesToRetrieve
        );
        if (algoliaProduct) {
            hotspot.hotspot.fields.product = algoliaProduct;
        }
    }

    return hotspot;
};

const processHotspot = async (
    hotspotsDesktop: IModuleHotspotFields["hotspotsDesktop"][],
    hotspotsMobile: IModuleHotspotFields["hotspotMobile"][],
    locale: string,
    pageType: PageTypes
) => {
    const [updatedDesktopHotspots, updatedMobileHotspots] = await Promise.all([
        Promise.all(
            hotspotsDesktop.map((hotspot) => processSingleHotspot(hotspot, locale, pageType))
        ),
        Promise.all(
            hotspotsMobile.map((hotspot) => processSingleHotspot(hotspot, locale, pageType))
        ),
    ]);

    return { updatedDesktopHotspots, updatedMobileHotspots };
};

export const updatePageHotspotModules = async (
    page: IPageHomepage | IPageProductCategoryPage | IPageStories | IPageCampaigns | IPageExplore,
    locale: string,
    pageType: PageTypes
) => {
    const clonedPage = structuredClone(page);

    const pageModules = clonedPage?.fields?.modules || [];

    for (const pageModule of pageModules) {
        if (pageModule?.sys?.contentType?.sys?.id === "moduleHotspot") {
            const hotspotsDesktop =
                (pageModule?.fields
                    ?.hotspotsDesktop as IModuleHotspotFields["hotspotsDesktop"][]) || [];
            const hotspotsMobile =
                (pageModule?.fields?.hotspotMobile as IModuleHotspotFields["hotspotMobile"][]) ||
                [];

            const { updatedDesktopHotspots, updatedMobileHotspots } = await processHotspot(
                hotspotsDesktop,
                hotspotsMobile,
                locale,
                pageType
            );

            pageModule.fields.hotspotsDesktop = updatedDesktopHotspots;
            pageModule.fields.hotspotMobile = updatedMobileHotspots;
        }
    }

    return clonedPage;
};

export const processPDPHotspot = async (
    additionalContent: Entry<IPdpConfigurationFields>,
    locale: string
): Promise<IPdpConfiguration> => {
    const clonedPdpConfiguration: IPdpConfiguration = structuredClone(additionalContent);

    const hotspotsFields = clonedPdpConfiguration?.fields?.hotspot?.fields;

    if (hotspotsFields) {
        const { updatedDesktopHotspots, updatedMobileHotspots } = await processHotspot(
            hotspotsFields.hotspotsDesktop as IModuleHotspotFields["hotspotsDesktop"][],
            hotspotsFields.hotspotMobile as IModuleHotspotFields["hotspotMobile"][],
            locale,
            PageTypes.PDP
        );

        hotspotsFields.hotspotsDesktop = updatedDesktopHotspots;
        hotspotsFields.hotspotMobile = updatedMobileHotspots;
    }

    return clonedPdpConfiguration;
};
